import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl, FormErrorMessage, FormLabel,
  HStack,
  Input,
  Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Select,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useMemo, useState, } from 'react';
import { v4 } from 'uuid';
import { useTranslation } from 'react-i18next';

import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, InputLimits } from '../../contexts/constants';
import { formatLocalDateTime } from '../../utils/utils';
import { getMultilingualDisplayName } from '../../utils/i18nDisplay';
import { supportedLngs } from '../../utils/i18n';
import {
  BilingualName,
  EquipmentSupplierType,
  Errors,
  LanguageCode
} from '../../contexts/types';

/** ステータスフィルタ型 */
type StatusFilter = 'all' | 'official' | 'candidate';

/**
 * マスタメンテナンス - サプライヤー 画面
 */
const EquipmentSupplier: React.FC = () => {
  const { t } = useTranslation();
  /** Toastメッセージを表示するためのフック */
  const toast = useToast();

  /** WebAPIContextを使用してAPI操作を行う */
  const WebAPI = useContext(WebAPIContext);

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.EquipmentSupplier.title');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "nowrap",
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル(2列目以降はleftの設定が必要) */
  const fixedColCellStyle = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
    boxShadow: "1px 0 0 #ccc",
  }

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 170,
  }

  /** 項目（値）幅 */
  const valueWidth = 475;

  /** SUPPORTED_LANGUAGES/supportedLngsに追従する動的言語リスト */
  const langs = Object.keys(supportedLngs) as LanguageCode[];
  /** GraphQL selectionSet用: "ja en" 等 */
  const langFields = langs.join(' ');

  /** サプライヤーマスタデータ */
  const [masterData, setMasterData] = useState<EquipmentSupplierType[]>([]);

  /** 編集対象サプライヤーマスタ */
  const [editTarget, setEditTarget] = useState<EquipmentSupplierType>({});

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** モーダル表示制御用：サプライヤー入力用のモーダル */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  // （フィルター）サプライヤー名を管理
  const [filterEquipmentSupplierName, setFilterEquipmentSupplierName] = useState<string>("");

  // （フィルター）ステータス
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  /** 仮登録件数 */
  const candidateCount = useMemo(() => {
    return (masterData ?? []).filter(m => m.isCandidate && !m.deleted).length;
  }, [masterData]);

  /** フィルター済みマスタデータ */
  const fillteredMasterData = (masterData ?? []).filter(m => {
    if (!m) {
      return false;
    }

    // 指定した値が含まれているかを返す
    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };

    // ステータスフィルタ
    if (statusFilter === 'official' && m.isCandidate) return false;
    if (statusFilter === 'candidate' && !m.isCandidate) return false;

    return langs.some((lang) => hasFilterString(m.equipmentSupplierName?.[lang], filterEquipmentSupplierName));
  });

  /**
   * 画面初期表示
   */
  useEffect(() => {
    const fetch = async () => {
      await fetchMasterData();
    }

    // 実行
    fetch();
  }, []);

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = async () => {
    onOpenEditModal();
  }

  /**
   * 指定したプロパティ名の値を変更
   * @param propertyName プロパティ名
   * @param value 値
   */
  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * 対象のマスタデータ取得
   */
  const fetchMasterData = async () => {
    try {
      const payload = {
        query: `
            query {
              getEquipmentSupplier(hasDeleted: true) {
                equipmentSupplierId
                equipmentSupplierName { ${langFields} }
                deleted
                isCandidate
                registeredBy
                createDate
                updateDate
              }
            }`
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentSupplier) {
        // サプライヤーマスタ情報（表示言語基準でソート）
        const sorted = [...response.data.getEquipmentSupplier].sort((a: EquipmentSupplierType, b: EquipmentSupplierType) =>
          getMultilingualDisplayName(a.equipmentSupplierName).localeCompare(getMultilingualDisplayName(b.equipmentSupplierName))
        );
        setMasterData(sorted);
      } else {
        console.error(`Failed to call API getEquipmentSupplier. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: TITLE }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `サプライヤー一覧取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「サプライヤー名」リンク押下
   * @param equipmentSupplierId サプライヤーID
   */
  const handleEdit = async (equipmentSupplierId?: string | null) => {
    try {
      const payload = {
        query: `
            query getEquipmentSupplierById($equipmentSupplierId: String) {
              getEquipmentSupplierById(equipmentSupplierId: $equipmentSupplierId) {
                equipmentSupplierId
                equipmentSupplierName { ${langFields} }
                deleted
                isCandidate
                registeredBy
                createDate
                updateDate
              }
            }`,
        variables: { equipmentSupplierId }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getEquipmentSupplierById) {
        // サプライヤーマスタ情報
        setEditTarget(response?.data?.getEquipmentSupplierById);
        // 編集モーダル表示
        onOpenEditModal();
      } else {
        console.error(`Failed to call API getEquipmentSupplierById. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchInfoFailed', { name: TITLE }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchData", error);
      toast({
        title: TITLE,
        description: `サプライヤー情報取得に失敗しました。`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    // 登録・更新
    if (!await putEditTarget()) {
      setIsSaving(false);
      return;
    }

    // 一覧更新
    await fetchMasterData();

    // モーダル閉じる
    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * バリデーションチェックを行う。
   * @returns true：検証成功、false：検証失敗
   */
  const validate = () => {
    setErrors({});

    const localErrors: Errors = {};

    // 必須項目チェック
    const required = (value?: string | null) => Boolean(value || null);

    // 必須項目チェックとエラー情報設定
    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string, errorKey: string = propertyName) => {
      const requiredResult = required(target?.[propertyName]);

      if (!requiredResult) {
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    }

    let result = true;

    // サプライヤー名の動的バリデーション (ja, en等)
    langs.forEach((lang) => {
      const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
      const errorKey = `equipmentSupplierName${CapitalizedLang}`;
      const translationKey = lang === 'ja'
        ? 'features.MasterMaintenance.EquipmentSupplier.field.name'
        : `features.MasterMaintenance.EquipmentSupplier.field.name${CapitalizedLang}`;

      if (!requiredAndSetError(editTarget.equipmentSupplierName, lang, t(translationKey), errorKey)) {
        result = false;
      } else if (lang !== 'ja' && editTarget.equipmentSupplierName?.[lang] && editTarget.equipmentSupplierName[lang]!.length > 100) {
        const tooLongTranslationKey = `features.MasterMaintenance.EquipmentSupplier.error.tooLongName${CapitalizedLang}`;
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t(tooLongTranslationKey),
        };
        result = false;
      }
    });

    setErrors(localErrors);
    return result;
  }

  /**
   * 入力したデータを登録・更新
   */
  const putEditTarget = async () => {
    try {
      // 登録パラメータ
      const input = {
        equipmentSupplierId: editTarget.equipmentSupplierId || `equipment-supplier-${v4()}`,
        equipmentSupplierName: langs.reduce((acc, lang) => {
          acc[lang] = editTarget.equipmentSupplierName?.[lang] || '';
          return acc;
        }, {} as BilingualName),
        deleted: editTarget.deleted,
        isCandidate: editTarget.isCandidate ?? false,
        updateDate: editTarget.updateDate,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation putEquipmentSupplier($input: EquipmentSupplierInput!) {
              putEquipmentSupplier(input: $input) {status}
            }
          `,
        variables: {
          input: input,
        }
      });

      if (result.data.putEquipmentSupplier.status === "success") {
        toast({
          title: TITLE,
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log(`Success to call API putEquipmentSupplier.`);
        return true;
      } else if (result?.data?.putEquipmentSupplier.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putEquipmentSupplier.`);
        return false;
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putEquipmentSupplier. errors = ${JSON.stringify(result?.errors)}`);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putEquipmentSupplier. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「確定する」ボタン押下（仮登録→正式採用）
   */
  const handlePromote = async () => {
    if (isSaving) return;
    setIsSaving(true);

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    // isCandidate を false にして保存
    const promoted = { ...editTarget, isCandidate: false };

    const input = {
      equipmentSupplierId: promoted.equipmentSupplierId,
      equipmentSupplierName: langs.reduce((acc, lang) => {
        acc[lang] = promoted.equipmentSupplierName?.[lang] || '';
        return acc;
      }, {} as BilingualName),
      deleted: promoted.deleted,
      isCandidate: false,
      updateDate: promoted.updateDate,
    };

    try {
      const result = await WebAPI.request({
        query: `
          mutation putEquipmentSupplier($input: EquipmentSupplierInput!) {
            putEquipmentSupplier(input: $input) {status}
          }
        `,
        variables: { input }
      });

      if (result?.data?.putEquipmentSupplier?.status === "success") {
        setEditTarget(promoted);

        toast({
          title: TITLE,
          description: t('common.message.promoteToOfficial'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // 一覧更新
        await fetchMasterData();

        // モーダル閉じる
        setIsSaving(false);
        onCloseEditModal();
      } else {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.EquipmentSupplier.message.confirmFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setIsSaving(false);
      }
    } catch (error) {
      console.error("handlePromote error:", error);
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.EquipmentSupplier.message.confirmError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsSaving(false);
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    // 一覧更新
    await fetchMasterData();
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   */
  const handleCloseComplete = () => {
    // 初期化
    setEditTarget({});
    setErrors({});
  }

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <HStack spacing={4} mb={2}>
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>

          {/* ステータスフィルター */}
          <HStack spacing={2}>
            <Text fontWeight="bold" whiteSpace="nowrap">{t('common.field.status')}:</Text>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              width="160px"
              size="sm"
            >
              <option value="all">{t('common.label.all')}</option>
              <option value="official">{t('common.label.official')}</option>
              <option value="candidate">{t('common.label.candidate')}</option>
            </Select>
            {candidateCount > 0 && (
              <Badge colorScheme="orange" fontSize="sm" px={2} py={1}>
                {t('common.label.candidate')}: {candidateCount}{t('common.label.count')}
              </Badge>
            )}
          </HStack>
        </HStack>
        <TableContainer overflowX="auto" overflowY="auto" h="calc(100dvh - 150px)" mt={2}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              {langs.map((lang) => (
                <col key={`col-equipmentSupplierName-${lang}`} width={lang === 'ja' ? 300 : 200} />
              ))}
              <col width={120} />
              <col width={120} />
              <col width={280} />
              <col width={280} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                {langs.map((lang) => {
                  const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                  const translationKey = lang === 'ja'
                    ? 'features.MasterMaintenance.EquipmentSupplier.field.name'
                    : `features.MasterMaintenance.EquipmentSupplier.field.name${CapitalizedLang}`;
                  return (
                    <Th {...headerCellStyle} {...(lang === 'ja' ? fixedColCellStyle : {})} key={`th-equipmentSupplierName-${lang}`}>
                      {t(translationKey)}
                      {lang === 'ja' && (
                        <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterEquipmentSupplierName} setFilterValue={setFilterEquipmentSupplierName} />
                      )}
                    </Th>
                  );
                })}
                <Th {...headerCellStyle}>{t('common.field.status')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.registrationDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {fillteredMasterData.map(record => {
                const bgColor = record.deleted ? "gray.100" : record.isCandidate ? "orange.50" : "white";

                return (
                  <Tr role="group" key={record.equipmentSupplierId} _hover={{ bg: "gray.200" }}>
                    {langs.map((lang) => (
                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...(lang === 'ja' ? fixedColCellStyle : {})} key={`td-equipmentSupplierName-${lang}-${record.equipmentSupplierId}`}>
                        {lang === 'ja' ? (
                          <Tooltip label={record.equipmentSupplierName?.[lang]} placement="top-start" hasArrow openDelay={500}>
                            <Button
                              variant="link"
                              textDecoration="underline"
                              color="blue.500"
                              onClick={(e) => {
                                e.currentTarget.blur();
                                handleEdit(record.equipmentSupplierId)
                              }}
                              sx={{
                                display: 'inline-block',
                                maxW: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {record.equipmentSupplierName?.[lang]}
                              {record.isCandidate && <Tag size="sm" colorScheme="orange" ml={1}>{t('common.label.candidate')}</Tag>}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip label={record.equipmentSupplierName?.[lang]} placement="top-start" hasArrow openDelay={500}>
                            <Text isTruncated >{record.equipmentSupplierName?.[lang]}</Text>
                          </Tooltip>
                        )}
                      </Td>
                    ))}
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">
                      {record.isCandidate ? (
                        <Tag size="sm" colorScheme="orange">{t('common.label.candidate')}</Tag>
                      ) : (
                        <Tag size="sm" colorScheme="blue">{t('common.label.official')}</Tag>
                      )}
                    </Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">{record.deleted ? (
                      <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
                    ) : (
                      <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
                    )
                    }</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updateDate)}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box >

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>{t('features.MasterMaintenance.EquipmentSupplier.modal.editTitle')}</Text>
              {editTarget.isCandidate && <Tag colorScheme="orange">{t('common.label.candidate')}</Tag>}
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack>

              {/* サプライヤー名（多言語）入力欄: SUPPORTED_LANGUAGES/supportedLngsに追従する動的ループ */}
              {langs.map((lang) => {
                const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                const errorKey = `equipmentSupplierName${CapitalizedLang}`;
                const fieldTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.EquipmentSupplier.field.name'
                  : `features.MasterMaintenance.EquipmentSupplier.field.name${CapitalizedLang}`;
                const placeholderTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.EquipmentSupplier.placeholder.name'
                  : `features.MasterMaintenance.EquipmentSupplier.placeholder.name${CapitalizedLang}`;

                return (
                  <FormControl isInvalid={errors[errorKey]?.isError} h={70} key={`fc-equipmentSupplierName-${lang}`}>
                    <Flex align="start">
                      <FormLabel style={labelStyle}>
                        {t(fieldTranslationKey)}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          value={editTarget.equipmentSupplierName?.[lang] || ""}
                          onChange={(event) => { changeEditTarget("equipmentSupplierName", { ...editTarget.equipmentSupplierName, [lang]: event.target.value }) }}
                          placeholder={t(placeholderTranslationKey)}
                          width={valueWidth}
                          maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                          isInvalid={errors[errorKey]?.isError}
                        />
                        <FormErrorMessage mt={0}>{errors[errorKey]?.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                );
              })}

              <FormControl isInvalid={errors.deleted?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.deleteFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }} isDisabled={true}
                        onClick={() => {
                          changeEditTarget("deleted", false);
                        }}>{t('common.label.notDeleted')}</Button>

                      <Switch
                        isChecked={editTarget.deleted}
                        onChange={(event) => { changeEditTarget("deleted", event.target.checked) }}
                        isInvalid={errors.deleted?.isError}
                        isDisabled={true}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }} isDisabled={true}
                        onClick={() => {
                          changeEditTarget("deleted", true)
                        }}>{t('common.label.deleted')}</Button>
                    </HStack>
                    <FormErrorMessage mt={0}>{errors.deleted?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              {editTarget.isCandidate && editTarget.registeredBy && (
                <FormControl>
                  <Flex align="start">
                    <FormLabel style={labelStyle}>
                      {t('common.label.registeredBy')}
                    </FormLabel>
                    <VStack align="left">
                      <Text>{editTarget.registeredBy}</Text>
                    </VStack>
                  </Flex>
                </FormControl>
              )}

              <FormControl isInvalid={errors.createDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.createDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.createDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.updateDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.updateDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.updateDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            {editTarget.isCandidate && editTarget.updateDate && (
              <Button colorScheme="green" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
                e.currentTarget.blur();
                handlePromote();
              }} m={1}>
                {t('common.button.confirmPromote')}
              </Button>
            )}
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default EquipmentSupplier;
